import React, { useState, useRef, useCallback } from 'react';
import './cards.css';

const RarePatchCard = ({ card, scale = 1, interactive = true, showBack = false }) => {
  const [flipped, setFlipped] = useState(false);
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    el.style.transform = `perspective(800px) rotateY(${(x - 0.5) * 12}deg)`;
    el.style.setProperty('--lx', `${50 + (0.5 - x) * 12}%`);
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = cardRef.current;
    if (el) el.style.transform = 'perspective(800px) rotateY(0deg)';
    if (el) { el.style.setProperty('--lx', '50%'); el.style.setProperty('--ly', '20%'); }
  }, []);

  const {
    name, team, teamFull, position, number,
    playerId, teamLogoId, sport = 'mlb',
    teamColor, nameplateColor,
    photoUrl, photoPosition = 'center 15%',
    fontSize = 18,
    patchColor = '#c41e3a',
    patchColor2,
    patchVariant = 3,
    bio = {},
    stats = [],
    statHeaders = [],
    highlights = [],
    cardNumber = '',
  } = card;

  const headshotUrl = sport === 'nhl'
    ? `https://assets.nhle.com/mugs/nhl/latest/${playerId}.png`
    : `https://img.mlbstatic.com/mlb-photos/image/upload/w_200,q_auto:best/v1/people/${playerId}/headshot/silo/current`;

  const actionUrl = photoUrl || (sport === 'nhl'
    ? `https://assets.nhle.com/mugs/actionshots/1296x729/${playerId}.jpg`
    : `./cards/photos/players/${playerId}.jpg`);

  const teamLogoUrl = sport === 'nhl'
    ? `https://assets.nhle.com/logos/nhl/svg/${team}_light.svg`
    : `https://www.mlbstatic.com/team-logos/${teamLogoId}.svg`;

  const leagueLogo = 'https://upload.wikimedia.org/wikipedia/commons/a/a6/Major_League_Baseball_logo.svg';

  return (
    <div className="bdc-card-outer bdc-dual-outer" ref={interactive ? cardRef : undefined} onMouseMove={interactive ? handleMouseMove : undefined} onMouseLeave={interactive ? handleMouseLeave : undefined} style={{ transition: interactive ? 'transform 0.25s ease-out' : 'none', ...(scale !== 1 ? { width: `${450 * scale}px`, height: `${320 * scale}px`, overflow: 'hidden' } : {}) }}>
      <div className="bdc-dual-scaler" style={scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'top left' } : undefined}>
      <div className={`bdc-dual-flipper ${flipped ? 'flipped' : ''}`} onClick={interactive ? () => setFlipped(!flipped) : undefined}>

        {/* FRONT */}
        <div className="bdc-card-face bdc-dual-front bdc-patch-front">
          <div className="bdc-patch-bg" />
          <div className="bdc-patch-pattern" />
          {interactive && <div className="bdc-shimmer" />}

          <div className="bdc-patch-photo">
            <img src={actionUrl} alt={name} style={{ objectPosition: photoPosition }} />
          </div>
          <div className="bdc-patch-divider" />

          {/* Patch area */}
          <div className="bdc-patch-area">
            <img className="bdc-patch-team-logo" src={teamLogoUrl} alt={team} onError={(e) => e.target.style.display='none'} />
            <div className="bdc-patch-window" style={{ background: '#27251F' }}>
              <div className="bdc-patch-fabric">
                <img className="bdc-patch-fabric-img" src="./cards/photos/fabric.jpg" alt="" style={{ transform: 'scale(1.3)' }} />
                <div className="bdc-patch-fabric-color" style={{ background: patchColor2 && patchVariant !== 3 ? (patchVariant === 1 ? `linear-gradient(to right, ${patchColor} calc(50% - 7px), ${patchColor2} calc(50% - 7px))` : `linear-gradient(to right, ${patchColor2} calc(50% + 7px), ${patchColor} calc(50% + 7px))`) : patchColor }} />
                {patchColor2 && patchVariant !== 3 && (
                  <div className="bdc-patch-fabric-slice" style={{ boxShadow: patchVariant === 1 ? 'inset 1px 0 3px rgba(0,0,0,0.3), inset -1px 0 3px rgba(0,0,0,0.3), -3px 0 6px rgba(0,0,0,0.6)' : 'inset 1px 0 3px rgba(0,0,0,0.3), inset -1px 0 3px rgba(0,0,0,0.3), 3px 0 6px rgba(0,0,0,0.6)' }} />
                )}
                <div className="bdc-patch-shadow-dynamic" />
              </div>
            </div>
          </div>

          <div className="bdc-dual-bottom" style={{ background: 'linear-gradient(to bottom, #ffffff, #f0f0f0, #e8e8e8)' }}>
            <div className="bdc-dual-names">
              <div className="bdc-dual-names-outline" style={{ background: nameplateColor || teamColor }} />
              <div className="bdc-dual-names-fill" />
              <span className="bdc-dual-name" style={{ fontSize: `${fontSize}px`, letterSpacing: '3px' }}>{name}</span>
            </div>
            <div className="bdc-dual-desc"><em>{teamFull}</em></div>
          </div>

          <img className="bdc-dual-league-logo" src={leagueLogo} alt="MLB" onError={(e) => e.target.style.display='none'} />
          <div className="bdc-bd-logo"><div className="bdc-bd-bar" /><div className="bdc-bd-tick" />BD</div>
        </div>

        {/* BACK */}
        {(interactive || showBack) && (
        <div className="bdc-card-face bdc-dual-back">
          <div className="bdc-dual-back-title">Rare Patch — /50</div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div className="bdc-dual-back-header">
              <img className="bdc-dual-back-headshot" src={headshotUrl} alt={name} onError={(e) => e.target.style.display='none'} />
              <div className="bdc-dual-back-info">
                <div className="bdc-dual-back-pname">{name}</div>
                <div className="bdc-dual-back-pmeta">{team} · {position} · #{number}</div>
              </div>
            </div>
            <table className="bdc-stats-table" style={{ fontSize: '9px' }}>
              <thead><tr>{statHeaders.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
              <tbody>
                {stats.map((row, i) => (
                  <tr key={i} className={i === stats.length - 1 ? 'highlight' : ''}>
                    {row.map((cell, j) => <td key={j}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {highlights.length > 0 && (
              <>
                <div className="bdc-stats-title" style={{ color: nameplateColor || teamColor, fontSize: '8px' }}>Highlights</div>
                <table className="bdc-stats-table">
                  <tbody>
                    {highlights.map((h, i) => (
                      <tr key={i}><td>{h.achievement}</td><td>{h.year}</td></tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
          <div className="bdc-back-footer">
            <div className="bdc-card-number">{cardNumber}</div>
            <div className="bdc-rarity-tag" style={{ background: '#2563eb' }}>Rare Patch /50</div>
          </div>
        </div>
        )}

      </div>
      </div>
    </div>
  );
};

export default React.memo(RarePatchCard);
